import { computed, nextTick, onMounted, ref, watch } from 'vue'

import { CopyrightStatus, FileRole } from '@/shared/graphql/router/graphql'
import type { File } from '@/shared/graphql/router/graphql'
import { getEntityInfo } from '@/shared/utils/fileHelpers'
import { useUpdateFileSettingsMutation } from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { updateFileInExplorerCache } from '@/features/explorer/utils/explorerCache'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { useDevice } from '@/shared/composables/useDevice'
import pluralize from 'pluralize'

const SHEET_SETTLE_MS = 500

export function useDescriptionFocus(enabled: () => boolean | undefined) {
  const descriptionField = ref<HTMLDivElement>()
  const { isMobile } = useDevice()

  onMounted(async () => {
    if (!enabled()) return
    await nextTick()
    const textarea = descriptionField.value?.querySelector('textarea')
    if (!textarea) return
    if (!isMobile.value) {
      textarea.focus()
      return
    }
    const keyboardHolder = createKeyboardHolder()
    window.setTimeout(() => {
      if (textarea.isConnected) {
        textarea.focus({ preventScroll: true })
        resetViewportShove()
      }
      keyboardHolder.remove()
    }, SHEET_SETTLE_MS)
  })

  return descriptionField
}

function createKeyboardHolder() {
  const input = document.createElement('input')
  input.type = 'text'
  input.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;border:0;padding:0;font-size:16px'
  document.body.appendChild(input)
  input.focus({ preventScroll: true })
  return input
}

function resetViewportShove() {
  const reset = () => {
    const doc = document.scrollingElement
    if (doc && doc.scrollTop > 0) doc.scrollTop = 0
    if (window.scrollY > 0) window.scrollTo(0, 0)
  }
  reset()
  window.setTimeout(reset, 300)
  window.setTimeout(reset, 700)
}

export function useFileSettings(initialFile: File, close: () => void) {
  const { mutate: updateFileSettings, loading } =
    useUpdateFileSettingsMutation()

  const toastStore = useToastStore()

  const file = ref({ ...initialFile })
  const title = ref(file.value.name)
  const description = ref(file.value.description || '')

  const titleError = ref('')

  watch(title, () => {
    titleError.value = ''
  })

  const isInactive = computed(
    () => file.value.copyright.status === CopyrightStatus.Inactive
  )

  const isOwner = computed(() => file.value.role === FileRole.Owner)
  const isCoOwner = computed(() => file.value.role === FileRole.CoOwner)
  const isAuthor = computed(() => file.value.role === FileRole.Author)

  const shouldDisable = computed(() => loading.value || isInactive.value)

  const canEditDetails = computed(() => isOwner.value && !shouldDisable.value)

  const manageActionCopy = computed(() => (isOwner.value ? 'Manage' : 'View'))

  const privateStatus = computed(() =>
    file.value.settings.isPrivate ? 'private' : 'public'
  )

  const owners = computed(() => {
    const result: { id: string; name: string; avatarUrl: string }[] = []
    const ownerInfo = file.value.owner
    if (ownerInfo && typeof ownerInfo === 'object' && 'id' in ownerInfo) {
      result.push(getEntityInfo(ownerInfo))
    }
    if (file.value.coOwners?.edges) {
      for (const edge of file.value.coOwners.edges) {
        if (edge.node.__typename === 'EntityWithRole') {
          result.push(getEntityInfo(edge.node.entity))
        }
      }
    }
    return result
  })

  const authors = computed(() => {
    const result: { id: string; name: string; avatarUrl: string }[] = []
    if (file.value.authors?.edges) {
      for (const edge of file.value.authors.edges) {
        if (edge.node.__typename === 'EntityWithRole') {
          result.push(getEntityInfo(edge.node.entity))
        }
      }
    }
    return result
  })

  const viewers = computed(() => {
    const result: { id: string; name: string; avatarUrl: string }[] = []
    if (file.value.viewers?.edges) {
      for (const edge of file.value.viewers.edges) {
        if (edge.node.__typename === 'ViewerEntity') {
          result.push(getEntityInfo(edge.node.entity))
        }
      }
    }
    return result
  })

  const ownersCount = computed(() => file.value.coOwners.totalCount + 1)
  const authorsCount = computed(() => file.value.authors.totalCount)
  const viewersCount = computed(() => file.value.viewers?.totalCount ?? 0)

  const ownersLinkCopy = computed(
    () => `${ownersCount.value} ${pluralize('owner', ownersCount.value || 0)}`
  )

  const authorsLinkCopy = computed(
    () =>
      `${authorsCount.value} ${pluralize('author', authorsCount.value || 0)}`
  )

  const invitesLinkCopy = computed(
    () =>
      `${viewersCount.value} ${pluralize('has', viewersCount.value || 0)} access`
  )

  const openChild = (dialog: Dialog) => {
    openDialog(dialog, {
      props: { file: file.value },
      stack: true,
      events: {
        success(data) {
          file.value = data as File
        },
      },
    })
  }

  const handleOwnersClick = () => openChild(Dialog.FileOwners)
  const handleAuthorsClick = () => openChild(Dialog.FileAuthors)
  const handleInvitesClick = () => openChild(Dialog.FileAccess)
  const handlePrivacyClick = () => openChild(Dialog.FilePrivacySettings)

  const save = async () => {
    if (!canEditDetails.value) return

    if (!title.value.trim()) {
      titleError.value = 'Please enter a file title'
      return
    }

    try {
      await updateFileSettings(
        {
          input: {
            id: file.value.id,
            name: title.value,
            description: description.value,
          },
        },
        {
          update() {
            updateFileInExplorerCache(file.value.id, {
              name: title.value,
              description: description.value,
            })
          },
        }
      )

      close()
    } catch (error) {
      if (error instanceof Error) {
        toastStore.showToast({
          type: ToastType.Error,
          content: 'Could not save copyright settings - please try again later',
          error: error,
        })
      }
    }
  }

  return {
    file,
    title,
    description,
    titleError,
    loading,
    isOwner,
    isCoOwner,
    isAuthor,
    shouldDisable,
    canEditDetails,
    manageActionCopy,
    privateStatus,
    owners,
    authors,
    viewers,
    authorsCount,
    viewersCount,
    ownersLinkCopy,
    authorsLinkCopy,
    invitesLinkCopy,
    handleOwnersClick,
    handleAuthorsClick,
    handleInvitesClick,
    handlePrivacyClick,
    save,
  }
}
